/**
 * RotatePositionDialog — admin assigns a user to a position kind in a department.
 * Atomically closes the prior holder (mock-api enforces).
 * Owner: Phase 7+ — V18 item A.
 */
import { useEffect, useState } from 'react';
import { Button } from '@edms/design-system';
import { useRotatePosition, type DepartmentPosition } from '../../api/orgHooks';
import styles from '../AuthorWorkbench/NewDocumentForm.module.css';

const KIND_OPTIONS: { value: DepartmentPosition['positionKind']; label: string }[] = [
  { value: 'DEPARTMENT_HEAD', label: 'Department head' },
  { value: 'DEPUTY_HEAD',     label: 'Deputy head' },
  { value: 'QA_MANAGER',      label: 'QA manager' },
  { value: 'AUTHOR',          label: 'Author' },
  { value: 'REVIEWER',        label: 'Reviewer' },
  { value: 'DOC_CONTROLLER',  label: 'Document controller' },
  { value: 'TRAINEE',         label: 'Trainee' },
];

export function RotatePositionDialog({
  isOpen, onClose, departmentId, departmentName,
  initialKind, onRotated,
}: {
  isOpen: boolean;
  onClose: () => void;
  departmentId: string;
  departmentName: string;
  initialKind?: DepartmentPosition['positionKind'];
  onRotated: (p: DepartmentPosition) => void;
}) {
  const [kind, setKind] = useState<DepartmentPosition['positionKind']>(initialKind ?? 'DEPARTMENT_HEAD');
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [backupUserId, setBackupUserId] = useState('');
  const [backupUserName, setBackupUserName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const m = useRotatePosition(departmentId);

  useEffect(() => {
    if (isOpen) {
      setKind(initialKind ?? 'DEPARTMENT_HEAD');
      setUserId(''); setUserName(''); setBackupUserId(''); setBackupUserName(''); setErr(null);
    }
  }, [isOpen, initialKind]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!userId.trim() || !userName.trim()) { setErr('User id + display name are required.'); return; }
    try {
      const p = await m.mutateAsync({
        positionKind: kind,
        userId: userId.trim(),
        userName: userName.trim(),
        backupUserId: backupUserId.trim() || undefined,
        backupUserName: backupUserName.trim() || undefined,
      });
      onRotated(p);
      onClose();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  if (!isOpen) return null;
  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.dialog} role="dialog" aria-modal="true">
        <header className={styles.header}>
          <h2 className={styles.title}>Rotate position</h2>
          <p className={styles.desc}>{departmentName}</p>
        </header>
        <form className={styles.form} onSubmit={onSubmit}>
          {err && <div className={styles.errorBanner} role="alert">{err}</div>}
          <div className={styles.field}>
            <label className={styles.label}>Position kind<span className={styles.required}>*</span></label>
            <select className={styles.select} value={kind} onChange={(e) => setKind(e.target.value as DepartmentPosition['positionKind'])}>
              {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary, #6B7989)', marginTop: 4 }}>
              Rotating closes the prior holder atomically (WF-037 history).
            </div>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Primary user id<span className={styles.required}>*</span></label>
            <input className={styles.input} value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="u-omar-saadi" />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Primary display name<span className={styles.required}>*</span></label>
            <input className={styles.input} value={userName} onChange={(e) => setUserName(e.target.value)} placeholder="Omar Al-Saadi" />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Backup user id</label>
            <input className={styles.input} value={backupUserId} onChange={(e) => setBackupUserId(e.target.value)} placeholder="u-mohanad-ali (optional)" />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Backup display name</label>
            <input className={styles.input} value={backupUserName} onChange={(e) => setBackupUserName(e.target.value)} placeholder="Dr. Mohanad Ali (optional)" />
          </div>
          <footer className={styles.footer}>
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" isLoading={m.isPending}>Rotate</Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
